import React, { useEffect, useMemo } from 'react';
import { formatCurrency, formatInt } from '../utils/format';
import { resolveWell, navigateToWell } from '../utils/wellIdentity';
import {
  buildDrillProvenance,
  clearDrillSelection,
  hasActiveDrill,
  parsePeriodToken,
  PERIOD_KIND,
  resolvePeriodDrill,
} from '../utils/drilldown';
import { track } from '../lib/track';

function ProvenanceList({ provenance }) {
  const rows = [
    ['Deal', provenance.dealId],
    ['As of', provenance.asOf],
    ['Generator', provenance.generatorVersion],
    ['Price deck', provenance.priceDeck],
    ['DuckDB', provenance.duckdb],
    ['VDR source', provenance.vdrSource],
    ['Surface', provenance.surface],
  ].filter(([, v]) => v);

  if (!rows.length && !provenance.streamMethod) {
    return <p className="drill-empty">No package provenance attached yet.</p>;
  }

  return (
    <dl className="drill-provenance">
      {rows.map(([label, value]) => (
        <div key={label} className="drill-provenance-row">
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
      {provenance.streamMethod ? (
        <div className="drill-provenance-row drill-provenance-wide">
          <dt>Stream method</dt>
          <dd>{provenance.streamMethod}</dd>
        </div>
      ) : null}
    </dl>
  );
}

function WellBody({ resolved }) {
  if (!resolved) {
    return <p className="drill-empty">Well not resolved in the loaded package / map index.</p>;
  }
  const { identity: idn, asset } = resolved;
  const refPv = asset?.ref?.pv10;
  return (
    <div className="drill-body-block">
      <h3 className="drill-entity-title">{idn.lease || 'Well'}</h3>
      <p className="drill-entity-sub">
        {[idn.status, idn.wellType, idn.county && `${idn.county} Co.`].filter(Boolean).join(' · ') ||
          'Identity from selection'}
        {idn.api ? ` · API ${idn.api}` : ''}
        {asset?.propnum ? ` · ${asset.propnum}` : ''}
      </p>
      <dl className="drill-metrics">
        <div>
          <dt>Seller PV10</dt>
          <dd>{refPv != null ? formatCurrency(refPv) : '—'}</dd>
        </div>
        <div>
          <dt>Net reserves</dt>
          <dd>
            {asset?.netGasResMmcf != null ? `${formatInt(asset.netGasResMmcf)} MMcf` : '—'}
          </dd>
        </div>
        <div>
          <dt>WI / NRI</dt>
          <dd>
            {idn.wi != null && idn.nri != null
              ? `${Number(idn.wi).toFixed(1)}% / ${Number(idn.nri).toFixed(1)}%`
              : '—'}
          </dd>
        </div>
        <div>
          <dt>Modeled</dt>
          <dd>{idn.modeled ? 'Yes' : 'No'}</dd>
        </div>
      </dl>
      <div className="drill-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigateToWell(idn.api || idn.lease || resolved.wellId)}
        >
          Open full well detail →
        </button>
      </div>
    </div>
  );
}

function formatMetricValue(metric) {
  if (metric == null) return '—';
  if (typeof metric.value === 'string') return metric.value;
  if (metric.format === 'currency') return formatCurrency(metric.value);
  if (metric.format === 'int') return formatInt(metric.value);
  return String(metric.value);
}

/** Period / reserves mark body — resolves typed `month` tokens (plain / disc: / hh:). */
function MonthBody({ month, pageData }) {
  const drill = resolvePeriodDrill(month, pageData);
  const kind = parsePeriodToken(month).kind;
  const emptyHint =
    kind === PERIOD_KIND.disc
      ? 'Discount-rate mark selected. Open Reserves for PV sensitivity values when that tab JSON is loaded.'
      : kind === PERIOD_KIND.hh
        ? 'Price-deck mark selected. Open Reserves for HH strip values when that tab JSON is loaded.'
        : 'Period selected. Open Cash Flow or Executive for the matching table row values when that tab\'s JSON is loaded.';

  return (
    <div className="drill-body-block" data-testid="period-drill-body" data-period-kind={drill.kind}>
      <h3 className="drill-entity-title">{drill.title || `Period ${month}`}</h3>
      <p className="drill-entity-sub">{drill.subtitle || 'Summary chart mark'}</p>
      {!drill.empty ? (
        <dl className="drill-metrics">
          {drill.metrics.map((metric) => (
            <div key={metric.label}>
              <dt>{metric.label}</dt>
              <dd>{formatMetricValue(metric)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="drill-empty">{emptyHint}</p>
      )}
    </div>
  );
}

function ScenarioBody({ scenarioId, dealPackage, pageData }) {
  const scenarios = dealPackage?.scenarios || pageData?.scenarios || [];
  const named = scenarios.find((s) => s.id === scenarioId || s.label === scenarioId);
  return (
    <div className="drill-body-block">
      <h3 className="drill-entity-title">{named?.label || scenarioId}</h3>
      <p className="drill-entity-sub">
        Named package scenario · id <code>{scenarioId}</code>
      </p>
      {named?.deltas ? (
        <dl className="drill-metrics">
          {Object.entries(named.deltas).map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{typeof v === 'number' ? v : String(v)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p className="drill-empty">
          Scenario id is in the deep link. Decision desk evaluates it against live knobs when that
          tab is open.
        </p>
      )}
      {named?.note ? <p className="engine-note">{named.note}</p> : null}
    </div>
  );
}

/**
 * In-page detail drawer for chart/map drill selection (#114).
 * Deep-link restore: mount whenever route selection has well/month/scenario.
 * Assistant context is wired in App via buildAssistantContext({ selectionContext }).
 */
export default function DetailDrawer({
  selection,
  surface = '',
  dealPackage = null,
  pageData = null,
  mapData = null,
  geoscienceData = null,
}) {
  const open = hasActiveDrill(selection);

  const provenance = useMemo(
    () =>
      buildDrillProvenance({
        dealPackage,
        pageData,
        selection,
        surface,
      }),
    [dealPackage, pageData, selection, surface],
  );

  const resolved = useMemo(() => {
    if (!selection?.selectedWellId) return null;
    return resolveWell(selection.selectedWellId, {
      dealPackage,
      mapWells: mapData?.wells || pageData?.wells || [],
      geoscienceFiles: geoscienceData?.files || geoscienceData?.lasFiles || [],
    });
  }, [selection?.selectedWellId, dealPackage, mapData, pageData, geoscienceData]);

  useEffect(() => {
    if (!open) return undefined;
    track('drill_drawer_opened', {
      surface: surface || undefined,
      well: selection.selectedWellId ? String(selection.selectedWellId).slice(0, 40) : undefined,
      month: selection.selectedMonth || undefined,
      scenario: selection.scenarioId || undefined,
      concern: selection.concernId || undefined,
    });
    return undefined;
  }, [
    open,
    surface,
    selection?.selectedWellId,
    selection?.selectedMonth,
    selection?.scenarioId,
    selection?.concernId,
  ]);

  if (!open) return null;

  const periodKind = selection.selectedMonth
    ? parsePeriodToken(selection.selectedMonth).kind
    : PERIOD_KIND.month;
  const title = selection.selectedWellId
    ? 'Well drilldown'
    : selection.selectedMonth
      ? periodKind === PERIOD_KIND.disc
        ? 'Discount-rate drilldown'
        : periodKind === PERIOD_KIND.hh
          ? 'Price-deck drilldown'
          : 'Period drilldown'
      : 'Scenario drilldown';

  const onClose = () => {
    track('drill_drawer_closed', { surface: surface || undefined });
    clearDrillSelection({ path: surface || undefined });
  };

  return (
    <div className="drill-drawer-root" role="presentation">
      <button type="button" className="drill-drawer-backdrop" aria-label="Close detail" onClick={onClose} />
      <aside
        className="drill-drawer"
        role="dialog"
        aria-modal="false"
        aria-label={title}
        data-testid="detail-drawer"
      >
        <header className="drill-drawer-header">
          <div>
            <div className="drill-drawer-kicker">Detail</div>
            <h2 className="drill-drawer-title">{title}</h2>
          </div>
          <button type="button" className="btn-secondary drill-drawer-close" onClick={onClose}>
            Close
          </button>
        </header>

        <div className="drill-drawer-body">
          {selection.selectedWellId ? <WellBody resolved={resolved} /> : null}
          {selection.selectedMonth ? (
            <MonthBody month={selection.selectedMonth} pageData={pageData} />
          ) : null}
          {selection.scenarioId ? (
            <ScenarioBody
              scenarioId={selection.scenarioId}
              dealPackage={dealPackage}
              pageData={pageData}
            />
          ) : null}

          {selection.concernId ? (
            <p className="engine-note">
              Linked concern <code>{selection.concernId}</code>
            </p>
          ) : null}

          <section className="drill-provenance-section">
            <div className="section-title">Provenance</div>
            <ProvenanceList provenance={provenance} />
          </section>

          <p className="drill-deeplink-hint">
            Deep link uses hash keys <code>well</code>, <code>month</code>, <code>scenario</code>
            {selection.concernId ? (
              <>
                , <code>concern</code>
              </>
            ) : null}
            . Refresh restores this drawer.
          </p>
        </div>
      </aside>
    </div>
  );
}
